import React from "react";

import {
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_ICON,
  SIDEBAR_WIDTH_MOBILE,
} from "./constants";
import { useSidebar } from "./hooks/useSidebar";
import { SidebarCollapsible, SidebarSide, SidebarVariant } from "./types";

interface SidebarProps {
  side?: SidebarSide;
  variant?: SidebarVariant;
  collapsible?: SidebarCollapsible;
  className?: string;
  children: React.ReactNode;
}

/**
 * Sidebar - Main sidebar container component
 *
 * With `collapsible="icon"` on a pointer device the sidebar reserves its own
 * width in the layout while it is pinned open, but the collapsed icon rail
 * expands as an *overlay* on hover: the rail keeps its 56px slot in the flow and
 * the expanded panel floats over the content, so nothing next to it reflows.
 *
 * @param side - Which side the sidebar appears on (left | right)
 * @param variant - Visual variant (default | floating | inset)
 * @param collapsible - Collapsible behavior (offcanvas | icon | none)
 * @param className - Additional CSS classes
 * @param children - Child components
 */
export const Sidebar = ({
  side = "left",
  variant = "default",
  collapsible = "offcanvas",
  className = "",
  children,
}: SidebarProps) => {
  const {
    state,
    open: isOpen,
    isMobile,
    setOpen,
    isHoverExpanded,
    setHoverExpanded,
  } = useSidebar();

  // Icon mode on a pointer device: the rail hover-expands over the content.
  const isHoverRail = collapsible === "icon" && !isMobile;

  // Base styles
  const baseStyles = "flex flex-col bg-accent border-solid text-primary";

  // Side positioning
  const sideStyles = side === "left" ? "left-0" : "right-0";

  // Variant styles
  const variantStyles = {
    default: "border-r border-primary",
    floating: "m-lg rounded-lg shadow-lg border border-primary",
    inset: "border-r border-primary",
  }[variant];

  // Collapsible behavior styles
  const collapsibleStyles = (() => {
    if (collapsible === "none") {
      return "relative";
    }

    if (isMobile) {
      // Mobile: Always offcanvas
      return `fixed top-0 bottom-0 z-50 transition-transform duration-300 ${
        isOpen
          ? "translate-x-0"
          : side === "left"
          ? "-translate-x-full"
          : "translate-x-full"
      }`;
    }

    if (collapsible === "offcanvas") {
      // Desktop offcanvas
      return `fixed top-0 bottom-0 z-40 transition-transform duration-300 ${
        isOpen
          ? "translate-x-0"
          : side === "left"
          ? "-translate-x-full"
          : "translate-x-full"
      }`;
    }

    if (collapsible === "icon") {
      // Desktop icon mode - pinned open it fills its own slot; collapsed it is
      // a rail that expands over the content on hover. Either way it is
      // positioned against the slot rendered below, so the content never moves.
      //
      // The stacking raise lasts only as long as the panel actually floats over
      // the content. Carrying it permanently put the sidebar above the app's
      // overlays — a drawer backdrop is z-10 — leaving the rail lit up over a
      // dimmed page. Hovering while an overlay is open is not a case to solve
      // for: the overlay covers the rail, so the pointer never reaches it.
      // z-20 clears what the panel overlaps in the left strip (the app header
      // at z-[5], the table's pinned headers at z-10) and nothing more.
      return `absolute top-0 bottom-0 transition-[width] duration-300 ${
        isHoverExpanded ? "z-20 shadow-lg" : ""
      }`;
    }

    return "relative";
  })();

  // Rendered width - using inline styles for dynamic values
  const width = (() => {
    if (isMobile) {
      return SIDEBAR_WIDTH_MOBILE;
    }

    if (collapsible === "icon" && state === "collapsed") {
      return SIDEBAR_WIDTH_ICON;
    }

    return SIDEBAR_WIDTH;
  })();

  // Width the sidebar occupies in the layout. Only the pinned state counts, so
  // a hover expansion overlays the content instead of pushing it aside.
  const slotWidth = isOpen ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_ICON;

  // Layout width the sidebar occupies, ignoring a hover overlay. CSS that
  // offsets content by the sidebar width keys off this, not `data-state`.
  const pinnedState = isOpen ? "expanded" : "collapsed";

  const sidebar = (
    <aside
      className={`
          ${baseStyles}
          ${sideStyles}
          ${variantStyles}
          ${collapsibleStyles}
          ${className}
        `.trim()}
      style={
        {
          width,
          "--sidebar-width": width,
        } as React.CSSProperties
      }
      onMouseEnter={
        isHoverRail && !isOpen ? () => setHoverExpanded(true) : undefined
      }
      onMouseLeave={isHoverRail ? () => setHoverExpanded(false) : undefined}
      data-state={state}
      data-pinned-state={pinnedState}
      data-side={side}
      data-variant={variant}
      data-collapsible={collapsible}
      data-mobile={isMobile}
      data-hover-expanded={isHoverExpanded}
    >
      {children}
    </aside>
  );

  return (
    <>
      {/* Backdrop for mobile */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 z-40 bg-secondary/60"
          onClick={() => setOpen(false)}
        />
      )}

      {isHoverRail ? (
        /* Layout slot: holds the rail's (or pinned panel's) width while the
           sidebar itself is positioned on top of it. */
        <div
          className="relative shrink-0 transition-[width] duration-300"
          style={{ width: slotWidth }}
          data-sidebar-slot
        >
          {sidebar}
        </div>
      ) : (
        sidebar
      )}
    </>
  );
};
